import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import { Box } from '@mui/material';
import { FC, useState } from 'react';
import { SDGTableHeader } from './sdg-table-header';
import { SDGTableBody } from './sdg-table-body';
import { CustomPagination } from 'components/shared/custom-pagination';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
    selectSDGAssessmentPageData,
    selectSDGAssessmentTableData,
    setInitialSDG,
    toggleSelected,
    updatePage,
    updateQaStatus,
    updateRowsPerPage,
} from 'store/slices/sdgAssessmentSlice';
import { mapSDGAssessmentListResponse } from './mapper';
import PopChat from 'components/popchat/popchat';
import { AssessmentCommentTarget } from 'api/hooks/assessment-comments/assessmentCommentsTypes';
import { useGetSDGAssessmentList } from 'api/hooks';
import { useParams } from 'react-router';
import { WithSpinner } from 'components/with-spinner';
import { useSDGAssessmentSave } from './use-sdg-assessment-save';
import { QaStatus } from 'api/hooks/commonTypes';
import { QaStatusComponent } from '../qa-status';

type Props = {
    isReadonly: boolean;
};

export const SDGAssessmentListTable: FC<Props> = ({ isReadonly }) => {
    const dispatch = useAppDispatch();
    const { assessmentId } = useParams();
    const sdgAssessmentTableData = useAppSelector(selectSDGAssessmentTableData);
    const data = mapSDGAssessmentListResponse(sdgAssessmentTableData);
    const [chatQuestionId, setChatQuestionId] = useState<string | null>(null);
    const [openChat, setOpenChat] = useState(false);
    const { page, rowsPerPage } = useAppSelector(selectSDGAssessmentPageData);
    const { isLoading } = useGetSDGAssessmentList({
        assessmentId: assessmentId ?? '',
        enabled: !!assessmentId && assessmentId !== '0',
        onSuccess: (data) => {
            dispatch(setInitialSDG(data));
        },
    });

    const { save } = useSDGAssessmentSave();

    const handleSetRowsPerPage = async (rowsPerPage: number) => {
        await save();
        dispatch(updateRowsPerPage(rowsPerPage));
    };

    const handleSetPage = async (page: number) => {
        await save();
        dispatch(updatePage(page));
    };

    const handleOpenChat = (id: string) => {
        setChatQuestionId(id);
        setOpenChat(true);
    };

    const handleCloseChat = () => {
        setChatQuestionId(null);
        setOpenChat(false);
    };

    const handleCheck = (id: string) => {
        dispatch(toggleSelected({ _id: id }));
    };

    const handleBulkStatusChange = (qaStatus: QaStatus) => {
        const selectedData = sdgAssessmentTableData.filter(
            (el) => el.isSelected
        );
        for (const data of selectedData) {
            dispatch(
                updateQaStatus({
                    _id: data.data._id,
                    qaStatus,
                })
            );
        }
    };

    const pagedData = data.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const isBulkUpdateVisible = sdgAssessmentTableData.some(
        (el) => el.isSelected
    );

    return (
        <WithSpinner isLoading={isLoading}>
            <Box>
                {isBulkUpdateVisible && (
                    <Box sx={{ mt: 2 }}>
                        <QaStatusComponent
                            label='Bulk Qa Status Update'
                            forceShow
                            handleClick={handleBulkStatusChange}
                        />
                    </Box>
                )}

                <Box sx={{ mt: 5 }}>
                    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                        <TableContainer>
                            <Table stickyHeader>
                                <SDGTableHeader />
                                <SDGTableBody
                                    data={pagedData}
                                    isReadOnly={isReadonly}
                                    handleOpenChat={handleOpenChat}
                                    onCheck={handleCheck}
                                />
                            </Table>
                        </TableContainer>
                        <CustomPagination
                            dataLength={data?.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            setPage={handleSetPage}
                            setRowsPerPage={handleSetRowsPerPage}
                        />
                    </Paper>
                    {openChat && (
                        <PopChat
                            questionId={chatQuestionId ?? ''}
                            closeChat={handleCloseChat}
                            target={AssessmentCommentTarget.SDG}
                            isReadOnly={isReadonly}
                        />
                    )}
                </Box>
            </Box>
        </WithSpinner>
    );
};
